import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { Settings } from 'src/app/app.settings.model';
import { AppData } from 'src/app/app.data';
import { SchedulerModel } from 'src/app/core/models/scheduler.model';
import { AppSettings } from 'src/app/app.settings';
import { ScopeService } from 'src/app/core/services/scope.service';
import { WaitListItemSatus, ApiResponseCode, ConfirmBoxAction } from 'src/app/core/enums';
import { WaitlistFilterModel } from 'src/app/core/models/waitlist-filter-model';
import { WaitlistService } from './waitlist.service';
import { WaitlistModel } from 'src/app/core/models/waitlist-model';
import { ConfirmBoxComponent } from '../confirm-box/confirm-box.component';
import { StandbyService } from '../standby/standby.service';
import { StandByModel } from 'src/app/core/interfaces/standby-model';
import { FindTimeModel } from 'src/app/core/models/find-time-model';
import { FindTimeService } from 'src/app/core/services/find-time.service';
import { FindTimeResultItem } from 'src/app/core/models/find-time-result-item-model';


@Component({
  selector: 'app-waitlist',
  templateUrl: './waitlist.component.html',
  styleUrls: ['./waitlist.component.scss']
})

export class WaitlistComponent implements OnInit, AfterViewInit, OnDestroy {

  public schedulers: SchedulerModel[] = [];
  public waitlistStatuses: { ID: number; Name: string }[] = [];
  public waitlistItems: WaitlistModel[] = [];
  private sbItem: StandByModel;

  public form: UntypedFormGroup;
  public settings: Settings;
  public error: String;
  public terminology: any = {};
  public actionType: ConfirmBoxAction;


  constructor(
    public appSettings: AppSettings,
    public fb: UntypedFormBuilder,
    public router: Router,
    public translate: TranslateService,
    private cookieService: CookieService,
    private adapter: DateAdapter<any>,
    private scopeService: ScopeService,
    private waitlistService: WaitlistService,
    private standbyService: StandbyService,
    private findTimeService: FindTimeService,
    private dialog: MatDialog,
    private appData: AppData,
  ) {

    this.settings = appSettings.settings;
    this.actionType = ConfirmBoxAction.DeleteWaitlistItem;

  }

  get f() { return this.form.controls; }


  ngOnInit() {
    // init waitlist controls
    this.form = this.fb.group({
      'fromDate': [moment().subtract(6, 'M'), Validators.required],
      'toDate': [moment().add(3, 'M'), Validators.required],
      'scheduler': [null],
      'status': null
    });

    // load previues form state
    this.loadState();
    // load waitlist statuses and scheduler list by user scope
    this.loadUserScope();
    // register form changes
    this.onChange();

  }

  ngOnDestroy() {
    // Called once, before the instance is destroyed.
    // Add 'implements OnDestroy' to the class.
  }

  ngAfterViewInit() {
    // Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    // Add 'implements AfterViewInit' to the class.
  }

  // load previues form state
  loadState() {
    //
  }

  // load waitlist statuses and scheduler list by user scope
  loadUserScope() {
    this.scopeService.getUserScope().subscribe(result => {
      if (result.Code === 1) {
        const data = result.Data;
        this.schedulers = data.schedulers;
        this.waitlistStatuses = this.convertStatusesEnumToObjectArray();
        this.terminology = data.terminology;

        // load waitlist items
        this.laodWaitListItems();
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }

  onChange() {
    // recognize if form change and reload waitlistItems
    this.form.valueChanges.subscribe(val => {
      this.laodWaitListItems();
    });
  }

  laodWaitListItems() {
    const filterData = this.getFilter();
    this.waitlistItems = [];
    this.waitlistService.loadItems(filterData).subscribe(result => {
      if (result.Code === 1) {
        this.waitlistItems = result.Data.map(item => new WaitlistModel(item, this.translate));
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }

  getFilter(): WaitlistFilterModel {
    // all scheduler in scope
    let schedulerIDs = this.schedulers.map(x => x.ID);

    // filter by selected scheduler if scheduler selected
    if (this.f.scheduler.value) {
      schedulerIDs = [parseInt(this.f.scheduler.value, 10)];
    }

    // by adding the utcOffset to the the date the server will get the same date (event converted to utc)
    // by adding utcOffset we send the same date after converted to utc
    const utcOffset = moment().utcOffset();
    const start = moment(this.f.fromDate.value).add(utcOffset, 'minutes');
    const end = moment(this.f.toDate.value).add(utcOffset, 'minutes');


    return new WaitlistFilterModel(
      schedulerIDs,
      new Date(start.toString()),
      new Date(end.toString()),
      this.f.status.value
    );

  }

  // convert the waitlist statuses enum into a translated array so we could iterate through it with *ngFor
  convertStatusesEnumToObjectArray(): { ID: number; Name: string }[] {
    const map: { ID: number; Name: string }[] = [];
    const statusesEnum = WaitListItemSatus;

    for (const status in statusesEnum) {
      if (typeof statusesEnum[status] === 'number' && <any>statusesEnum[status] !== 0) {
        const statusName = this.translate.instant(`WAITLIST-STATUS.${status.toLocaleUpperCase()}`);
        map.push({ ID: <any>statusesEnum[status], Name: statusName });
      }
    }
    return map;
  }

  onCreateAppointment(waitlistItem: WaitlistModel) {
    this.standbyService.GetModelByWaitListItemID(waitlistItem.SchedulerWaitListID).subscribe(result => {
      if (result.Code === ApiResponseCode.Success) {
        this.sbItem = result.Data;
        this.appData.waitlistItem = waitlistItem;
        this.appData.customerDetails = waitlistItem.Customer;
        this.buildModelForFindTime(this.sbItem);
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }

  buildModelForFindTime(Item: StandByModel) {

    // Data to send to server
    const model: FindTimeModel = new FindTimeModel();
    model.FirstDayOfWeek = Item.FirstDayOfWeek;
    model.CustomerID = Item.CustomerID;
    model.Date = moment(Item.FromDate);
    model.ToDate = moment(Item.ToDate);
    model.Duration = Item.Duration;
    model.ExpertiesID = Item.UserPractiseID.toString();
    model.BranchID = Item.BranchID;

    model.SchedulerIDForUser = Item.SchedulerID;

    model.SchedulerIDForRoom = +Item.SchedulerResourceID > 0 ? Item.SchedulerResourceID : null;
    model.StartTime = Item.FromTime;
    model.EndTime = Item.ToTime;
    model.WeekDays = [
      Item.WeekDays[0] || false,
      Item.WeekDays[1] || false,
      Item.WeekDays[2] || false,
      Item.WeekDays[3] || false,
      Item.WeekDays[4] || false,
      Item.WeekDays[5] || false,
      Item.WeekDays[6] || false
    ];

    this.findTimeService.findTimeGroup(model).subscribe(result => {
      if (result.Code === ApiResponseCode.Success) {
        this.appData.findTimeGroupResult = result.Data.map(item => new FindTimeResultItem(item));
        this.appData.treatments = Item.Treatments;
        this.appData.priceListCodes = Item.PriceListRefs;
        this.showAvailableSlotsResult();
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }

  showAvailableSlotsResult() {
    this.router.navigate(['/find-time-group-result']);
  }

  onDelete(waitlistItem: WaitlistModel) {
    // prompt deletion confirmation message.
    // get locale message for dialog
    const title = this.translate.instant('CONFIRM.DELETE-WL-TITLE');
    const msg = this.translate.instant('CONFIRM.DELETE-WL-MSG', {
      patName: waitlistItem.CustomerName
    });

    // open confiramation dialog to delete
    const dialogRef = this.dialog.open(ConfirmBoxComponent, {
      width: '300px',
      direction: (this.settings.rtl ? 'rtl' : null),
      data: {
        actionType: this.actionType,
        title: title,
        message: msg
      }
    });

    // delete if user click the Ok button
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // ok
        this.deleteWaitListItem(waitlistItem);
      }
    });
  }

  deleteWaitListItem(waitlistItem: WaitlistModel) {
    this.waitlistService.removeItem(waitlistItem.SchedulerWaitListID).subscribe(result => {
      if (result.Code === ApiResponseCode.Success) {
        // on success clear appointment from list
        const index = this.waitlistItems.indexOf(waitlistItem, 0);
        if (index > -1) {
          this.waitlistItems.splice(index, 1);
        }
      } else {
        this.error = result.error;
      }
    }, error => {
      this.error = error;
    });
  }

}
